import type {
    AutoFrameI,
    FlexFrameI,
    FrameI,
    GroupStyleI,
    ImageI,
    EllipseI,
    ItemI,
    TickerI,
} from "tweenly"
import Box from "@mui/material/Box"
import { t } from "i18next"
import { fillProperties } from "../../../../data/styles/groups/fill.styles"
import { useObjectGroupActions } from "../../../../hooks/useObjectGroupActions"
import AddButton from "../../../common/AddButton"
import ExpandableSection from "../../../common/ExpandableSection"
import EmptyObject from "../../../common/EmptyObject"
import FillRow from "./FillRow"

interface Props {
    object: ItemI | EllipseI | ImageI | TickerI | FrameI | FlexFrameI | AutoFrameI
    handleSetStyleValue: (property: any, value: any) => void
    handleSetObjectValue: (property: any, value: any) => void
    handleSetAttributeValue: (property: any, value: any) => void
    expanded?: boolean
}

const Fill = ({
    object,
    handleSetStyleValue,
    handleSetObjectValue,
    handleSetAttributeValue,
    expanded = true,
}: Props) => {
    const { addObjectGroup, updateObjectGroups, deleteObjectGroup } = useObjectGroupActions()

    const handleAddGroup = (e: any) => {
        if (!e) e = window.event
        e.cancelBubble = true
        if (e.stopPropagation) e.stopPropagation()
        const updatedGroups = addObjectGroup("fill", object["fill"])
        handleSetObjectValue("fill", updatedGroups)
    }

    const handleSetGroup = (index: number, property: any, value: any) => {
        const updatedGroups = updateObjectGroups(object["fill"], index, property, value)
        handleSetObjectValue("fill", updatedGroups)
    }

    const handleDeleteGroup = (index: number) => {
        const updatedGroups = deleteObjectGroup(object["fill"], index)
        handleSetObjectValue("fill", updatedGroups)
    }

    const fill: GroupStyleI[] | undefined = object.fill
    if (!fill) return <></>

    return (
        <ExpandableSection
            expanded={expanded}
            title={t("editor:colorTool.title")}
            secondaryActionComponent={
                <AddButton
                    text="fill"
                    handleAddObject={handleAddGroup}
                    disabled={fill.length >= 1 ? true : false}
                />
            }
            component={
                <Box width="100%" pb={0.5}>
                    {fill.length === 0 ? (
                        <EmptyObject text="No fills" />
                    ) : (
                        <Box display="flex" flexDirection="column" gap={1.5}>
                            {fill.map((group) => (
                                <FillRow
                                    key={group.index}
                                    fill={group}
                                    properties={fillProperties}
                                    handleOnChange={(field, value) =>
                                        handleSetGroup(group.index, field, value)
                                    }
                                    handleDelete={() => handleDeleteGroup(group.index)}
                                    handleSetStyleValue={handleSetStyleValue}
                                    handleSetAttributeValue={handleSetAttributeValue}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            }
        />
    )
}

export default Fill
